import { Ratio } from "@/libs/units/percentage";
import type { Point } from "@incytestudios/ddv";
import {
  getMotorCurrentA,
  getMotorEfficiencyRatio,
  getMotorPowerFactor,
  getMotorSpeedRPM
} from "../../calculations/motors";
import type { MotorPerformanceChartContext } from "./d";

export const getMotorPerformancePoints = (
  context: MotorPerformanceChartContext
): {
  efficiency: Point;
  powerFactor: Point;
  current: Point;
  nameplateCurrent: Point;
}[] => {
  // All datasets share the same x axis
  // Motor eff, motor PF, and motor current share Y domain
  const {
    motor,
    pumpHz,
    minLoad,
    maxLoad,
    iterations,
    motorVoltageCorrections
  } = context;
  const points = [];

  if (!motor) return [];

  const INCREMENT = (maxLoad - minLoad) / iterations;

  for (let i = 0; i <= iterations; i++) {
    const motorLoadRatio = minLoad + INCREMENT * i;

    const eff = getMotorEfficiencyRatio(
      motorLoadRatio,
      motor.effb,
      motor.eff1,
      motor.eff2,
      motor.eff3,
      motor.eff4,
      motor.eff5,
      motorVoltageCorrections.efficiencyCorrection
    );

    const current = getMotorCurrentA(
      motorLoadRatio,
      1,
      motor.ib,
      motor.i1,
      motor.i2,
      motor.i3,
      motor.i4,
      motor.i5,
      motorVoltageCorrections.ampCorrection
    );

    const powerFactor = getMotorPowerFactor(
      motorLoadRatio,
      motor.pfb,
      motor.pf1,
      motor.pf2,
      motor.pf3,
      motor.pf4,
      motor.pf5,
      motorVoltageCorrections.powerFactorCorrection
    );

    const npCurrent = getMotorSpeedRPM(
      motorLoadRatio,
      pumpHz,
      motor.nb,
      motor.n1,
      motor.n2,
      motor.n3,
      motor.n4,
      motor.n5,
      motorVoltageCorrections.rpmCorrection
    );

    const xaxis = Ratio.toStandardUnit(motorLoadRatio);

    points.push({
      efficiency: {
        x: xaxis,
        y: Ratio.toStandardUnit(eff)
      },
      powerFactor: {
        x: xaxis,
        y: Ratio.toStandardUnit(powerFactor)
      },
      current: {
        x: xaxis,
        y: Ratio.toStandardUnit(current)
      },
      nameplateCurrent: { x: xaxis, y: npCurrent }
    });
  }

  return points;
};
